import {Injectable} from '@angular/core';
import {BrowserSessionStateId, DeviceId, ExpeditionId} from '@webmate-web-core/shared/types/ids';
import {Browser} from '@webmate-web-core/shared/types/browser';
import {combineLatest, EMPTY, merge, Observable, of, throwError, TimeoutError} from 'rxjs';
import {BrowserSessionInfo, InteractionPath} from '@webmate-web-core/shared/types/browsersession';
import {EventsService} from '@webmate-web-core/shared/services/events/events.service';
import {catchError, debounceTime, distinctUntilChanged, map, shareReplay, switchMap, take} from 'rxjs/operators';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {CDPClient} from '../../../webmate/shared/console/cdp/cdp-client';
import {AuthService} from '@webmate-web-core/shared/services/auth.service';
import {WorkbenchContextService} from './workbench-context.service';
import {WorkbenchConsoleService} from './workbench-console.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {
    qualitySenseFactRequestsForBrowser,
    supportsWorkbenchQualitySense,
    WORKBENCH_QUALITY_SENSE_POLICY
} from '../quality-sense';
import {WorkbenchPluginManager} from './workbench-plugin-manager.service';

/**
 * Filter for the active browser session queries. Extension point for filtering by properties of the
 * corresponding device: additional criteria would be resolved by joining {@link BrowserSessionInfo#optDeviceId}
 * against the project devices (DeviceService) before matching.
 */
export interface ActiveExpeditionsFilter {
    deviceId?: DeviceId;
}

/** Active sessions per project are few; the expeditions endpoint requires a page limit. */
const ACTIVE_EXPEDITIONS_QUERY_LIMIT = 100;

/** Coalesce bursts of session state changes (e.g. requested→starting→ready→running) into one refresh. */
const ACTIVE_EXPEDITIONS_REFRESH_DEBOUNCE_MS = 300;

/** Max wait for a freshly started session on an already-deployed device to become active. */
const EXPEDITION_READY_TIMEOUT_MS = 90000;

/**
 * Headless sessions first need their backing device deployed before the session starts, so allow a
 * more generous budget than a start on an already-deployed device.
 */
const HEADLESS_SESSION_READY_TIMEOUT_MS = 120000;

interface CdpConnection {
    expeditionId: ExpeditionId;
    client: CDPClient;
    sessionId?: string;
}

@Injectable()
export class WorkbenchExpeditionService {

    // Provided in WorkbenchSharedModule and instantiated together with the workbench UI.
    //
    // This service owns the lifecycle of the active browser session but holds no context state itself —
    // the active session lives in the workbench context. It changes only through explicit,
    // user-controlled entry points:
    //  - starting a session (console toolbar, start-session action, interaction paths, startup config)
    //  - selecting a session (topbar session switcher, browser sessions view)
    //  - opening a device console whose device has a running session (derived below)
    // It is cleared when its session terminates and pinned while the expedition lock is held.

    /** CDP connection cache for the active session — replaced when the active session changes. */
    private cdpConnection?: CdpConnection;

    /**
     * Shared live stream of the project's active browser sessions. One fetch per (debounced) state change
     * is multicast to every consumer (topbar switcher, console toolbars, browser sessions view, and the
     * follow-device logic), instead of each subscriber querying the backend on its own.
     */
    private sharedActiveExpeditions$?: Observable<BrowserSessionInfo[]>;

    constructor(private browserSessionService: BrowserSessionService,
                private authService: AuthService,
                private notificationService: WmNotificationService,
                private workbenchConsoleService: WorkbenchConsoleService,
                private eventService: EventsService,
                private readonly workbenchContextService: WorkbenchContextService,
                private readonly deviceService: DeviceService,
                private readonly pluginManager: WorkbenchPluginManager) {
        this.clearActiveExpeditionWhenTerminated();
        this.deriveActiveExpeditionFromOpenDevice();
    }

    /**
     * One-shot query for all currently active browser sessions in the project, proxying the
     * project-wide expeditions endpoint. Sessions terminated on the backend are filtered out.
     */
    getActiveExpeditionsInProject$(sessionFilter?: ActiveExpeditionsFilter): Observable<BrowserSessionInfo[]> {
        return this.fetchActiveExpeditions$().pipe(
            map(sessions => sessions.filter(session => this.matchesFilter(session, sessionFilter)))
        );
    }

    /**
     * Live variant of {@link getActiveExpeditionsInProject$}: emits the current active sessions
     * immediately and re-emits whenever a browser session in the project changes its state. Backed by a
     * single shared query (see {@link sharedActiveExpeditions$}), so multiple subscribers do not each hit
     * the backend.
     */
    observeActiveExpeditionsInProject$(sessionFilter?: ActiveExpeditionsFilter): Observable<BrowserSessionInfo[]> {
        return this.activeExpeditions$().pipe(
            map(sessions => sessions.filter(session => this.matchesFilter(session, sessionFilter)))
        );
    }

    getCDPClientForActiveExpedition(): Observable<CDPClient> {
        const activeExpedition = this.getActiveExpedition();
        if (!activeExpedition) {
            return throwError(() => 'Requested CDPClient but got no active expedition');
        }
        return CDPClient.create(activeExpedition.id, this.authService);
    }

    /**
     * Send CDP command to the active browser session.
     * @param method
     * @param params
     */
    sendCDPCommand(method: string, params?: any): Observable<any> {
        const activeExpedition = this.getActiveExpedition();
        if (!activeExpedition?.id) {
            return throwError(() => 'No active browser session available.');
        }
        return this.connectCDP(activeExpedition.id).pipe(
            switchMap(connection => connection.client.sendCommand(method, params, connection.sessionId))
        );
    }

    analyzeStateInActiveExpedition(): Observable<BrowserSessionStateId> {
        const activeExpedition = this.getActiveExpedition();
        if (!activeExpedition?.id) {
            return throwError(() => 'No active browser session available.');
        }
        return this.browserSessionService.analyzeState(activeExpedition.id);
    }

    /**
     * Quick-start a browser session on a specific device/console with the given browser, make it the
     * active session and open its console. Used by the console toolbar and the no-session affordance
     * in analysis tools.
     */
    startSessionOnActiveConsole$(deviceId: DeviceId, browser: Browser, startUrl?: string): Observable<BrowserSessionInfo> {
        const projectId = this.workbenchContextService.getProjectId();
        if (!this.pluginManager.isPluginRegistered('quality-sense')) {
            return this.activateExpeditionWhenReady$(
                this.browserSessionService.startBrowserExpeditionOnDevice$(projectId, deviceId, browser, startUrl)
            );
        }
        return this.deviceService.getDevice$(deviceId).pipe(
            take(1),
            switchMap(device => {
                const cdpAvailable = device.properties?.[DevicePropertyNames.CDPAvailable] === true;
                const qualitySenseSupported = supportsWorkbenchQualitySense(browser, cdpAvailable);
                if (!cdpAvailable && supportsWorkbenchQualitySense(browser, true)) {
                    console.warn(
                        `Device ${deviceId} does not advertise cdp.available; ` +
                        'QualitySense monitoring is disabled for this browser session.'
                    );
                }
                return this.activateExpeditionWhenReady$(
                    this.browserSessionService.startBrowserExpeditionOnDevice$(
                        projectId,
                        deviceId,
                        browser,
                        startUrl,
                        qualitySenseFactRequestsForBrowser(browser, cdpAvailable),
                        undefined,
                        qualitySenseSupported
                    )
                );
            })
        );
    }

    /**
     * Quick-start a lightweight headless browser session in the project, make it the active session and
     * open its console. Used by the no-session affordance in analysis tools, where a headless session is
     * sufficient and no device or browser selection is required.
     */
    startHeadlessSession$(startUrl?: string): Observable<BrowserSessionInfo> {
        const projectId = this.workbenchContextService.getProjectId();
        const qualitySenseEnabled = this.pluginManager.isPluginRegistered('quality-sense');
        return this.activateExpeditionWhenReady$(
            this.browserSessionService.startHeadlessBrowserExpeditionInProject$(
                projectId,
                startUrl,
                qualitySenseEnabled ? WORKBENCH_QUALITY_SENSE_POLICY.requestedFacts : undefined,
                true
            ),
            HEADLESS_SESSION_READY_TIMEOUT_MS
        );
    }

    /**
     * Opens the given interaction path in the console. If there is an active expedition, the interaction path will be executed in this expedition.
     * Otherwise, a new expedition will be started to execute the interaction path.
     */
    openInteractionPathInConsole$(interactionPath: InteractionPath): Observable<void> {
        const activeExpedition = this.getActiveExpedition();
        if (activeExpedition) {
            return this.browserSessionService.executeInteractionPath$(activeExpedition.id, interactionPath);
        }
        const projectId = this.workbenchContextService.getProjectId();
        const qualitySenseEnabled = this.pluginManager.isPluginRegistered('quality-sense');
        // Replaying an interaction path only needs a lightweight session, so start a headless one.
        return this.browserSessionService.startHeadlessBrowserExpeditionInProjectWithInteractionPaths$(
            projectId,
            [interactionPath],
            qualitySenseEnabled ? WORKBENCH_QUALITY_SENSE_POLICY.requestedFacts : undefined,
            true
        ).pipe(
            switchMap(expeditionId => this.activateExpeditionWhenReady$(of(expeditionId), HEADLESS_SESSION_READY_TIMEOUT_MS)),
            map(() => undefined)
        );
    }

    /**
     * Opens the given interaction path in the console. If there is an active expedition, the interaction path will be executed in this expedition.
     * On error, a notification will be shown.
     */
    openInteractionPathInConsole(interactionPath: InteractionPath): void {
        this.openInteractionPathInConsole$(interactionPath).subscribe({
            error: error => {
                if (error instanceof TimeoutError) {
                    this.notificationService.toastErrorNotification('Error launching browser: timeout');
                } else {
                    this.notificationService.toastErrorNotification('Error launching browser: ' + (error.message || 'unknown'));
                }
                console.error('Error opening audit target in browser', error);
            }
        });
    }

    /**
     * Wait for a freshly started expedition to become ready (at most 90 seconds), make it the
     * active browser session and open its console.
     */
    private activateExpeditionWhenReady$(expeditionId$: Observable<ExpeditionId>,
                                         readyTimeoutMs: number = EXPEDITION_READY_TIMEOUT_MS): Observable<BrowserSessionInfo> {
        return this.browserSessionService.awaitBrowserSessionReady$(expeditionId$, readyTimeoutMs).pipe(
            switchMap(expedition => {
                this.workbenchContextService.setContextActiveExpedition(expedition);
                return this.workbenchConsoleService.openConsoleForExpedition(expedition.id).pipe(map(() => expedition));
            })
        );
    }

    private matchesFilter(session: BrowserSessionInfo, sessionFilter?: ActiveExpeditionsFilter): boolean {
        return !sessionFilter?.deviceId || session.optDeviceId === sessionFilter.deviceId;
    }

    private getActiveExpedition(): BrowserSessionInfo | undefined {
        return this.workbenchContextService.getCurrentWorkbenchContext()?.expeditionContext?.activeExpedition;
    }

    /** Single backend query for the project's active browser sessions. */
    private fetchActiveExpeditions$(): Observable<BrowserSessionInfo[]> {
        const projectId = this.workbenchContextService.getProjectId();
        return this.browserSessionService.getExpeditionsInProject$(projectId, ACTIVE_EXPEDITIONS_QUERY_LIMIT).fetch().pipe(
            map(page => page.result.filter(session => session.isActive()))
        );
    }

    /**
     * Lazily-created shared stream of the project's active browser sessions: an immediate initial query
     * plus a re-query on each debounced burst of state changes, multicast via shareReplay so all
     * consumers share one backend request. A failed query is swallowed (last value is retained) so the
     * stream survives transient errors.
     */
    private activeExpeditions$(): Observable<BrowserSessionInfo[]> {
        if (!this.sharedActiveExpeditions$) {
            this.sharedActiveExpeditions$ = merge(
                of(undefined),
                this.sessionStateChanges$().pipe(debounceTime(ACTIVE_EXPEDITIONS_REFRESH_DEBOUNCE_MS))
            ).pipe(
                switchMap(() => this.fetchActiveExpeditions$().pipe(
                    catchError(error => {
                        console.error('Could not query active browser sessions', error);
                        return EMPTY;
                    })
                )),
                shareReplay({bufferSize: 1, refCount: true})
            );
        }
        return this.sharedActiveExpeditions$;
    }

    private sessionStateChanges$(): Observable<BrowserSessionInfo | undefined> {
        return this.eventService.getEventsOfTypeForProject(this.workbenchContextService.getProjectId(), 'BrowserSession.StateChanged').pipe(
            map(event => BrowserSessionInfo.fromBackendObject(event.session))
        );
    }

    /**
     * Clear the active browser session when its session terminates (e.g. stopped externally), so the
     * workbench does not keep a dead session active. Deliberately independent of any console being
     * active.
     */
    private clearActiveExpeditionWhenTerminated(): void {
        this.sessionStateChanges$().subscribe(sessionInfo => {
            if (!sessionInfo || sessionInfo.isActive()) {
                return;
            }
            if (this.getActiveExpedition()?.id === sessionInfo.id) {
                this.workbenchContextService.setContextActiveExpedition(undefined);
            }
        });
    }

    /**
     * The active browser session follows the open device console: opening a console is a user decision,
     * so when the console's device has a running session, that session becomes the active one; when it
     * has none, the active session is cleared. Re-derived when the active console changes, when the
     * expedition lock is released, and when a session on the open device changes state. While the lock
     * is held the active session is pinned and left untouched. A running session whose console is
     * merely closed is deliberately preserved (no active console → no derivation).
     */
    private deriveActiveExpeditionFromOpenDevice(): void {
        const openDeviceAndLock$ = this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            map(context => ({
                deviceId: context.deviceContext?.activeConsole ? context.deviceContext?.activeDevice ?? undefined : undefined,
                locked: !!context.expeditionContext?.activeExpeditionLocked
            })),
            distinctUntilChanged((a, b) => a.deviceId === b.deviceId && a.locked === b.locked)
        );

        // Re-evaluate when the open console/lock changes or when the shared active-sessions list updates.
        combineLatest([openDeviceAndLock$, this.activeExpeditions$()]).subscribe(([{deviceId, locked}, sessions]) => {
            if (!deviceId || locked) {
                return;
            }
            const currentActiveExpedition = this.getActiveExpedition();
            // Keep the current active session if it already belongs to the open device. Besides being the
            // desired behavior, this avoids a race right after starting a session, where the freshly
            // activated session may not yet appear in the (debounced) project query.
            if (currentActiveExpedition?.optDeviceId === deviceId) {
                return;
            }
            const chosenExpedition = sessions.find(session => session.optDeviceId === deviceId);
            if (chosenExpedition?.id !== currentActiveExpedition?.id) {
                this.workbenchContextService.setContextActiveExpedition(chosenExpedition);
            }
        });
    }

    private connectCDP(expeditionId: ExpeditionId): Observable<CdpConnection> {
        if (this.cdpConnection?.expeditionId === expeditionId) {
            return of(this.cdpConnection);
        }
        this.cdpConnection?.client.disconnect();
        this.cdpConnection = undefined;
        return CDPClient.create(expeditionId, this.authService).pipe(
            switchMap(client => client.sendCommand('Target.getTargets').pipe(
                map(response => response.targetInfos.filter((targetInfo: any) => targetInfo.type === 'page')),
                map(targetInfos => ({client: client, targetInfos: targetInfos}))
            )),
            switchMap(({client, targetInfos}) => {
                if (targetInfos.length === 0) {
                    return throwError(() => 'No page target available.');
                } else if (targetInfos.length > 1) {
                    console.warn('More than one page target available. Using first one.');
                }
                const targetId = targetInfos[0].targetId;
                return client.sendCommand('Target.attachToTarget', {targetId: targetId, flatten: true}).pipe(
                    map(result => ({client: client, targetId: targetId, sessionId: result.sessionId}))
                );
            }),
            switchMap(({client, targetId, sessionId}) =>
                client.sendCommand('Target.activateTarget', {targetId: targetId}).pipe(map(() => ({client: client, sessionId: sessionId})))
            ),
            map(({client, sessionId}) => {
                this.cdpConnection = {expeditionId: expeditionId, client: client, sessionId: sessionId};
                return this.cdpConnection;
            })
        );
    }
}
